import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { Device, StorageReport, AlertThreshold, HardwareInfo } from "../types";
import { useSort, sortRows, SortableTh } from "./SortableTh";
import { parseUtc } from "../utils/datetime";

/* Hardware Monitor — per-device live health dashboard.
 *
 * Renders every wave-A/B sensor reading the agent collects, color-
 * coded by the same thresholds the alert evaluator uses. Sections
 * collapse to "N/A" when the underlying sensor isn't detectable on
 * the host (e.g. fans on a workstation that doesn't expose them via
 * WMI, or RAID on a box with no controller).
 *
 * Threshold-driven coloring: we fetch /api/v1/alerts/thresholds once
 * on mount and key off ``metric`` to color each tile. Per-customer
 * overrides ARE applied — see the API; if there's no override the
 * global default lands automatically.
 */

type Severity = "ok" | "info" | "warning" | "critical" | "na";

type Direction = "normal" | "reverse" | "binary";

function severityFor(
  value: number | null | undefined,
  t: AlertThreshold | undefined,
  direction: Direction = "normal",
): Severity {
  if (t == null || value == null || direction === "binary") return "na";
  if (direction === "reverse") {
    if (value <= t.critical_pct) return "critical";
    if (value <= t.warn_pct) return "warning";
    if (value <= t.info_pct) return "info";
    return "ok";
  }
  if (value >= t.critical_pct) return "critical";
  if (value >= t.warn_pct) return "warning";
  if (value >= t.info_pct) return "info";
  return "ok";
}

function pillClass(s: Severity): string {
  switch (s) {
    case "critical":
      return "bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800";
    case "warning":
      return "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800";
    case "info":
      return "bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800";
    case "ok":
      return "bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800";
    default:
      return "bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600";
  }
}

function pillLabel(s: Severity): string {
  return s === "na" ? "N/A" : s.toUpperCase();
}

function fmtMinutesAgo(iso?: string | null): string | null {
  if (!iso) return null;
  // The API returns zone-less UTC ("2026-08-26T13:49:59.810138"), which
  // `new Date()` parses as LOCAL time. On any zone behind UTC that put every
  // timestamp in the future, so `ms` went negative and this returned null -
  // rendering "never" for every device even though the data was minutes old.
  // parseUtc() treats an offset-less stamp as the UTC it actually is.
  //
  // Deliberately parseUtc and not utcMillis: utcMillis returns 0 for
  // unparseable input, which would render as "20000 d ago" instead of "never".
  const d = parseUtc(iso);
  if (!d) return null;
  const ms = Date.now() - d.getTime();
  if (!isFinite(ms)) return null;
  // A small negative is clock skew between the agent, the appliance and the
  // browser - not "no data". Treat it as fresh rather than falling back to
  // "never", which is the more misleading of the two.
  if (ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}

function Tile({
  title,
  value,
  unit,
  severity,
  subtitle,
  extra,
  onClick,
}: {
  title: string;
  value: string;
  unit?: string;
  severity: Severity;
  subtitle?: string | null;
  extra?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 ${
        onClick ? "cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition" : ""
      }`}
      title={onClick ? "View running processes" : undefined}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">
          {title}
          {onClick && <span className="ml-1 text-gray-300 dark:text-gray-600">⤢</span>}
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${pillClass(severity)}`}>
          {pillLabel(severity)}
        </span>
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        {value}
        {unit && <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">{unit}</span>}
      </div>
      {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</div>}
      {extra && <div className="mt-2">{extra}</div>}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 mt-4">{children}</h3>;
}

/** Why there are no power supplies to show.
 *
 *  An empty list has two completely different meanings and this used to
 *  render one sentence for both: "No PSU monitoring detected (Dell OMSA /
 *  ipmitool not installed on this host)". On a virtual machine that is
 *  simply wrong - a guest has no power supplies and never will - and on a
 *  laptop it is noise. It also named two vendors in the console, which the
 *  product does not do anywhere else.
 */
function psuEmptyMessage(reason?: string | null): string {
  switch (reason) {
    case "virtual_machine":
      return "Virtual machine — no physical power supplies.";
    case "no_bmc":
      return "This machine has no management controller, so power supplies cannot be read.";
    case "bmc_unreachable":
      return "A management controller is present but did not respond. Power supply status is unavailable.";
    case "no_psu_sensors":
      return "The management controller reports no power supply sensors.";
    case "collector_error":
      return "Power supply status could not be collected. It will be retried at the next inventory.";
    case "no_source":
      return "No power supply telemetry is available on this machine.";
    default:
      // Agents older than the IPMI collector send no reason at all.
      return "Power supply status has not been reported by this device.";
  }
}

/** Why there are no fan or temperature readings.
 *
 * DERIVED FROM THE PSU REASON, deliberately, rather than from a new field.
 * "Is this a guest" and "does this machine have a management controller" are
 * facts about the MACHINE, not about its power supplies - the fans, the
 * chassis probes and the supplies are all read from the same BMC in the same
 * SDR walk. So the answer is already on the device and re-deriving it would
 * be a second copy that could disagree.
 *
 * The message this replaced was "Not detected via WMI on this hardware",
 * which was worse than unhelpful: it blamed the hardware while the real
 * cause was that we were asking Win32_Fan, which returns nothing on server
 * hardware. Eight physical servers read as "your hardware cannot do this"
 * for months. A monitoring tool that explains a gap with a wrong reason
 * stops anyone from ever looking again.
 */
function thermalEmptyMessage(
  reason: string | null | undefined,
  hasPsus: boolean,
  kind: "fan" | "temperature",
): string {
  const what = kind === "fan" ? "Fan speeds" : "Temperatures";
  switch (reason) {
    case "virtual_machine":
      return `Virtual machine — no physical ${kind === "fan" ? "fans" : "temperature probes"}.`;
    case "no_bmc":
      return `This machine has no management controller, so ${what.toLowerCase()} cannot be read.`;
    case "bmc_unreachable":
      return `A management controller is present but did not respond. ${what} are unavailable.`;
  }
  if (hasPsus) {
    // The BMC answered for the supplies, so it is reachable - it simply
    // publishes no sensor of this type, or none we can convert to real
    // units. That is a different fact from "we cannot reach it".
    return `The management controller reports no readable ${kind} sensors.`;
  }
  return `${what} have not been reported by this device.`;
}

export default function HardwareMonitor({
  device,
  storage,
  canManage = false,
  hardware = null,
}: {
  device: Device;
  storage: StorageReport | null;
  canManage?: boolean;
  /** Inventory, so the live percentages have a denominator. Null until the
   *  agent has reported hardware at least once. */
  hardware?: HardwareInfo | null;
}) {
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([]);
  const ioSort = useSort("name", "asc");
  const smartSort = useSort("model", "asc");
  const [killing, setKilling] = useState<number | null>(null);
  const [killMsg, setKillMsg] = useState("");

  // "End task" — queue a kill_process command, poll it, surface the result.
  // The agent re-verifies the live process name before killing (PID-reuse
  // safety), so a stale snapshot can't take out the wrong process.
  const killProcess = async (pid: number, name: string) => {
    if (
      !window.confirm(
        `End "${name}" (PID ${pid}) on ${device.hostname}? The process is force-terminated. ` +
          `Unsaved work in that process is lost.`,
      )
    )
      return;
    setKilling(pid);
    setKillMsg("");
    try {
      const r = await api.post(`/api/v1/devices/${device.id}/processes/kill`, { pid, name });
      const cid = r.data.command_id;
      for (let i = 0; i < 15; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        const s = await api.get(`/api/v1/devices/${device.id}/command/${cid}`);
        if (s.data.status === "completed") {
          setKillMsg(`Ended ${name} (PID ${pid}). Refresh to update the list.`);
          break;
        }
        if (s.data.status === "failed") {
          setKillMsg(s.data.error_message || `Could not end ${name} (PID ${pid}).`);
          break;
        }
      }
    } catch (e: any) {
      setKillMsg(e.response?.data?.detail || "Failed to queue End task");
    } finally {
      setKilling(null);
      setTimeout(() => setKillMsg(""), 9000);
    }
  };

  // Full process list drill-down (clicking the CPU or Memory tile).
  type Proc = { pid: number; name: string; cpu: number; mem_mb: number; disk_kbps?: number; user?: string };
  const [procOpen, setProcOpen] = useState(false);
  const [procLoading, setProcLoading] = useState(false);
  const [procError, setProcError] = useState("");
  const [procRows, setProcRows] = useState<Proc[]>([]);
  const [procTotal, setProcTotal] = useState(0);
  const [procSearch, setProcSearch] = useState("");
  const procSort = useSort("mem_mb", "desc");

  // focus: which column the drill-down opens sorted by (the tile that was
  // clicked — CPU / Memory / Disk I/O). Falls back to memory.
  const loadProcesses = async (focus: "cpu" | "mem_mb" | "disk_kbps" = "mem_mb") => {
    setProcOpen(true);
    setProcLoading(true);
    setProcError("");
    procSort.setSort({ key: focus, dir: "desc" });
    try {
      const r = await api.post(`/api/v1/devices/${device.id}/processes/list`);
      const cid = r.data.command_id;
      for (let i = 0; i < 20; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        const s = await api.get(`/api/v1/devices/${device.id}/command/${cid}`);
        if (s.data.status === "completed") {
          setProcRows(s.data.result?.processes || []);
          setProcTotal(s.data.result?.total || 0);
          setProcLoading(false);
          return;
        }
        if (s.data.status === "failed") {
          setProcError(s.data.error_message || "Could not list processes.");
          setProcLoading(false);
          return;
        }
      }
      setProcError("Timed out waiting for the device.");
    } catch (e: any) {
      setProcError(e.response?.data?.detail || "Failed to list processes.");
    } finally {
      setProcLoading(false);
    }
  };

  const killFromList = async (pid: number, name: string) => {
    await killProcess(pid, name);
    // Optimistically drop the row; a manual reopen re-fetches fresh.
    setProcRows((rows) => rows.filter((p) => p.pid !== pid));
  };

  useEffect(() => {
    api
      .get("/api/v1/alerts/thresholds")
      .then((r) => setThresholds(r.data))
      .catch(() => setThresholds([]));
  }, []);

  // Effective threshold lookup: per-customer override wins if present,
  // otherwise global default. The API already returns both rows; we
  // collapse here.
  const tMap = useMemo(() => {
    const out: Record<string, AlertThreshold> = {};
    for (const t of thresholds) {
      const existing = out[t.metric];
      // Prefer customer-specific rows over globals when both present
      // and the device's customer matches.
      if (
        !existing ||
        (t.customer_id != null && t.customer_id === device.customer_id)
      ) {
        out[t.metric] = t;
      }
    }
    return out;
  }, [thresholds, device.customer_id]);

  const lastMetricsAgo = fmtMinutesAgo(device.last_metrics_at);
  const lastHwAgo = fmtMinutesAgo(device.last_hw_health_at);

  // ── CPU ──
  const cpuPct = device.last_cpu_percent;
  const cpuSev = severityFor(cpuPct, tMap["cpu_used_pct"], "normal");
  const cpuSubtitle = device.cpu_high_since
    ? `Critical band since ${fmtMinutesAgo(device.cpu_high_since)} (sustained → critical at 10 min)`
    : lastMetricsAgo
      ? `Last sample ${lastMetricsAgo}`
      : null;

  // ── Memory ──
  const memPct = device.last_memory_percent;
  const memSev = severityFor(memPct, tMap["memory_used_pct"], "normal");

  /* The totals behind the percentages.
   *
   * cpu_threads is null for any device whose agent predates 1.0.78, so this
   * says "8 cores" rather than inventing a thread count - and on the machines
   * that do report it, "8 cores / 16 threads" is the number an engineer sizing
   * a workload actually wants. cpu_cores itself was under-counted on
   * multi-socket boxes before that agent (it read the first socket only), so
   * sockets are shown when there is more than one.
   */
  const cpuShape = (() => {
    if (!hardware?.cpu_cores) return null;
    const parts = [`${hardware.cpu_cores} cores`];
    if (hardware.cpu_threads) parts.push(`${hardware.cpu_threads} threads`);
    if (hardware.cpu_sockets && hardware.cpu_sockets > 1)
      parts.push(`${hardware.cpu_sockets} sockets`);
    return parts.join(" · ");
  })();

  const memSubtitle = (() => {
    const freePct = memPct != null && memPct >= 0 ? `${(100 - memPct).toFixed(0)}% free` : null;
    if (!hardware?.ram_gb) return freePct;
    // Absolute GB free is more actionable than a percentage when you are
    // deciding whether something will fit.
    const freeGb =
      memPct != null && memPct >= 0
        ? `${((hardware.ram_gb * (100 - memPct)) / 100).toFixed(1)} GB free`
        : null;
    return [freeGb || freePct, `of ${hardware.ram_gb} GB`].filter(Boolean).join(" ");
  })();

  // ── Temperatures ──
  const cpuTemp = device.last_cpu_temp_c;
  const chassisTemp = device.last_chassis_temp_c;
  const cpuTempSev = severityFor(cpuTemp, tMap["cpu_temp_c"], "normal");
  const chassisTempSev = severityFor(chassisTemp, tMap["chassis_temp_c"], "normal");

  // ── Disk capacity (per local drive) ──
  const drives = storage?.local_drives || [];

  // ── Disk I/O ──
  const diskIo = device.last_disk_io;
  const maxLat = diskIo?.max_latency_ms ?? null;
  const diskIoSev = severityFor(maxLat, tMap["disk_io_latency_ms"], "normal");

  // ── Battery ──
  const battery = device.last_battery_health_pct;
  const batterySev = device.has_battery
    ? severityFor(battery, tMap["battery_health_pct"], "reverse")
    : "na";

  // ── UPS ──
  const ups = device.last_ups_status;
  const upsPresent = !!ups?.present;
  const upsHealthSev = upsPresent
    ? severityFor(ups?.charge_pct ?? null, tMap["ups_health_pct"], "reverse")
    : "na";
  const upsStateSev: Severity = !upsPresent
    ? "na"
    : (ups?.status || "").toLowerCase() === "online"
      ? "ok"
      : (ups?.status || "").toLowerCase() === "on_battery"
        ? "warning"
        : (ups?.status || "").toLowerCase() === "replace"
          ? "critical"
          : "na";

  // ── Fans ──
  const fans = device.last_fan_status || [];
  const badFans = fans.filter(
    (f) => (f.status || "").toLowerCase() !== "ok" && (f.status || "") !== "",
  );
  const fanSev: Severity =
    fans.length === 0
      ? "na"
      : badFans.length === 0
        ? "ok"
        : badFans.some((f) => ["error", "failed"].includes((f.status || "").toLowerCase()))
          ? "critical"
          : "warning";

  // ── RAID + PSU collections (per-controller / per-PSU severity is
  // rendered inline in their respective sections below).
  const controllers = device.last_raid_status?.controllers || [];
  const psus = device.last_psu_status?.psus || [];

  // ── Disk SMART ──
  const smart = device.last_disk_health || [];

  const noLiveData = device.last_metrics_at == null && device.last_hw_health_at == null;

  return (
    <div className="space-y-2">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div>
          Live metrics:{" "}
          <strong className="text-gray-900 dark:text-gray-100">{lastMetricsAgo || "never"}</strong>
        </div>
        <div>
          Hardware inventory:{" "}
          <strong className="text-gray-900 dark:text-gray-100">{lastHwAgo || "never"}</strong>
        </div>
        <div className="ml-auto text-gray-400 dark:text-gray-500">
          Tiles read live from the agent; "N/A" = sensor not detectable on this hardware.
        </div>
      </div>

      {noLiveData && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300">
          No checkins or hardware inventory recorded yet for this device.
        </div>
      )}

      {/* ── Live metrics ── */}
      <SectionHeader>Live metrics</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Tile
          title="CPU Usage"
          value={cpuPct != null ? cpuPct.toFixed(0) : "—"}
          unit={cpuPct != null ? "%" : undefined}
          severity={cpuSev}
          // "62%" means nothing without knowing 62% of what. The threshold
          // note still wins when there is one - a CPU pegged for 20 minutes
          // is the more urgent fact - and the shape backfills otherwise.
          subtitle={cpuSubtitle || cpuShape}
          onClick={() => loadProcesses("cpu")}
        />
        <Tile
          title="Memory Usage"
          value={memPct != null ? memPct.toFixed(0) : "—"}
          unit={memPct != null ? "%" : undefined}
          severity={memSev}
          subtitle={memSubtitle}
          onClick={() => loadProcesses("mem_mb")}
        />
        <Tile
          title="Disk I/O (max latency)"
          value={maxLat != null ? maxLat.toFixed(0) : "—"}
          unit={maxLat != null ? "ms" : undefined}
          severity={diskIoSev}
          subtitle={
            diskIo?.per_disk?.length
              ? `${diskIo.per_disk.length} disk(s) sampled`
              : null
          }
          onClick={() => loadProcesses("disk_kbps")}
        />
      </div>

      {device.top_processes &&
        ((device.top_processes.by_cpu?.length ?? 0) > 0 ||
          (device.top_processes.by_mem?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(["by_cpu", "by_mem"] as const).map((key) => {
              const rows = device.top_processes?.[key] || [];
              if (rows.length === 0) return null;
              const isCpu = key === "by_cpu";
              return (
                <div
                  key={key}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4"
                >
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Top processes by {isCpu ? "CPU" : "memory"}
                  </div>
                  <table className="min-w-full text-xs">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {rows.map((p, i) => (
                        <tr key={i} className="text-gray-700 dark:text-gray-300 group">
                          <td className="py-1 pr-2 truncate max-w-[9rem]" title={`${p.name} (PID ${p.pid})`}>
                            {p.name}
                          </td>
                          <td className="py-1 pl-2 text-right font-mono whitespace-nowrap">
                            {isCpu ? `${p.cpu?.toFixed(1)}%` : `${p.mem_mb?.toFixed(0)} MB`}
                          </td>
                          {canManage && (
                            <td className="py-1 pl-2 text-right whitespace-nowrap">
                              <button
                                onClick={() => p.pid != null && killProcess(p.pid, p.name)}
                                disabled={killing === p.pid || p.pid == null}
                                className="text-[11px] text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-40 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={`End ${p.name} (PID ${p.pid})`}
                              >
                                {killing === p.pid ? "…" : "End task"}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {canManage && killMsg && (
                    <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-2">{killMsg}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {diskIo?.per_disk && diskIo.per_disk.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <SortableTh label="Disk" sortKey="name" sort={ioSort.sort} onSort={ioSort.toggle} />
                <SortableTh label="Read latency" sortKey="read_latency_ms" sort={ioSort.sort} onSort={ioSort.toggle} />
                <SortableTh label="Write latency" sortKey="write_latency_ms" sort={ioSort.sort} onSort={ioSort.toggle} />
                <SortableTh label="Queue length" sortKey="queue_length" sort={ioSort.sort} onSort={ioSort.toggle} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
              {sortRows(diskIo.per_disk, ioSort.sort, (d, key) => {
                switch (key) {
                  case "read_latency_ms": return d.read_latency_ms;
                  case "write_latency_ms": return d.write_latency_ms;
                  case "queue_length": return d.queue_length;
                  default: return (d.name || "").toLowerCase();
                }
              }).map((d, i) => (
                <tr key={i}>
                  <td className="px-3 py-1.5 font-mono text-gray-700 dark:text-gray-300">{d.name}</td>
                  <td className="px-3 py-1.5">{d.read_latency_ms.toFixed(1)} ms</td>
                  <td className="px-3 py-1.5">{d.write_latency_ms.toFixed(1)} ms</td>
                  <td className="px-3 py-1.5">{d.queue_length.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Temperatures ── */}
      <SectionHeader>Temperatures</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* A dash with no explanation reads as "this feature is broken".
            Say which of the several reasons applies. */}
        <Tile
          title="CPU Temperature"
          value={cpuTemp != null ? cpuTemp.toFixed(1) : "—"}
          unit={cpuTemp != null ? "°C" : undefined}
          severity={cpuTempSev}
          subtitle={
            cpuTemp == null
              ? thermalEmptyMessage(
                  device.last_psu_status?.reason,
                  psus.length > 0,
                  "temperature",
                )
              : null
          }
        />
        <Tile
          title="Chassis Temperature"
          value={chassisTemp != null ? chassisTemp.toFixed(1) : "—"}
          unit={chassisTemp != null ? "°C" : undefined}
          severity={chassisTempSev}
          subtitle={
            chassisTemp == null
              ? thermalEmptyMessage(
                  device.last_psu_status?.reason,
                  psus.length > 0,
                  "temperature",
                )
              : null
          }
        />
      </div>

      {/* ── Disk capacity ── */}
      <SectionHeader>Disk Capacity</SectionHeader>
      {drives.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 text-xs text-gray-500 dark:text-gray-400">
          No drives reported yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {drives.map((d, i) => {
            const total = d.total_gb || 0;
            const free = d.free_gb ?? 0;
            const usedPct = total > 0 ? ((total - free) / total) * 100 : 0;
            const sev = severityFor(usedPct, tMap["disk_used_pct"], "normal");
            return (
              <Tile
                key={i}
                title={`Drive ${d.letter}: ${d.label ? `(${d.label})` : ""}`}
                value={usedPct.toFixed(0)}
                unit="%"
                severity={sev}
                subtitle={`${free.toFixed(0)} GB free of ${total.toFixed(0)} GB`}
                extra={
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded h-1.5 overflow-hidden">
                    <div
                      className={
                        "h-full " +
                        (sev === "critical"
                          ? "bg-red-500"
                          : sev === "warning"
                            ? "bg-amber-500"
                            : sev === "info"
                              ? "bg-blue-500"
                              : "bg-green-500")
                      }
                      style={{ width: `${Math.min(100, Math.max(0, usedPct))}%` }}
                    />
                  </div>
                }
              />
            );
          })}
        </div>
      )}

      {/* ── SMART ── */}
      <SectionHeader>Disk SMART Health</SectionHeader>
      {smart.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 text-xs text-gray-500 dark:text-gray-400">
          No SMART data yet.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <SortableTh label="Model" sortKey="model" sort={smartSort.sort} onSort={smartSort.toggle} />
                <SortableTh label="Media" sortKey="media" sort={smartSort.sort} onSort={smartSort.toggle} />
                <SortableTh label="Health" sortKey="health" sort={smartSort.sort} onSort={smartSort.toggle} />
                <SortableTh label="Operational" sortKey="operational" sort={smartSort.sort} onSort={smartSort.toggle} />
                <SortableTh label="Serial" sortKey="serial" sort={smartSort.sort} onSort={smartSort.toggle} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(smart, smartSort.sort, (d, key) => {
                switch (key) {
                  case "media": return (d.media || "").toLowerCase();
                  case "health": return (d.health || "").toLowerCase();
                  case "operational": return (d.operational || "").toLowerCase();
                  case "serial": return (d.serial || "").toLowerCase();
                  default: return (d.model || "").toLowerCase();
                }
              }).map((d, i) => {
                const sev: Severity =
                  (d.health || "").toLowerCase() === "healthy"
                    ? "ok"
                    : (d.health || "").toLowerCase() === "warning"
                      ? "warning"
                      : (d.health || "").toLowerCase() === "unhealthy"
                        ? "critical"
                        : "na";
                return (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{d.model}</td>
                    <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{d.media || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${pillClass(sev)}`}
                      >
                        {d.health || "unknown"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{d.operational || "—"}</td>
                    <td className="px-3 py-1.5 text-gray-400 dark:text-gray-500 font-mono">{d.serial || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Fans ── */}
      <SectionHeader>Fans</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Tile
          title="Fan Health"
          value={fans.length === 0 ? "—" : `${fans.length - badFans.length} / ${fans.length}`}
          unit={fans.length > 0 ? "OK" : undefined}
          severity={fanSev}
          subtitle={
            fans.length === 0
              ? thermalEmptyMessage(
                  device.last_psu_status?.reason,
                  psus.length > 0,
                  "fan",
                )
              : badFans.length > 0
                ? `Bad: ${badFans.map((f) => f.name || "?").slice(0, 3).join(", ")}`
                : "All fans reporting OK"
          }
        />
      </div>

      {/* ── Battery + UPS ──
          "Battery", not "Power". This screen already has a "Power Supplies"
          section below, and two sections both named Power-something meant
          the reader had to open both to find out which held the UPS. */}
      <SectionHeader>Battery</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {device.has_battery ? (
          <Tile
            title="Battery Health"
            value={battery != null ? battery.toFixed(0) : "—"}
            unit={battery != null ? "% of design" : undefined}
            severity={batterySev}
            subtitle="Design vs full-charge capacity"
          />
        ) : (
          <Tile
            title="Battery"
            value="—"
            severity="na"
            subtitle="No battery on this device"
          />
        )}
        {upsPresent ? (
          <>
            <Tile
              title="UPS Battery"
              value={ups?.charge_pct != null ? ups.charge_pct.toFixed(0) : "—"}
              unit={ups?.charge_pct != null ? "%" : undefined}
              severity={upsHealthSev}
              subtitle={
                ups?.runtime_minutes != null ? `Est. runtime ~${ups.runtime_minutes} min` : null
              }
            />
            <Tile
              title="UPS Status"
              value={(ups?.status || "unknown").replace("_", " ")}
              severity={upsStateSev}
              subtitle={ups?.name || null}
            />
          </>
        ) : (
          <Tile title="UPS" value="—" severity="na" subtitle="No UPS reported on this device" />
        )}
      </div>

      {/* ── RAID ── */}
      <SectionHeader>RAID Controllers</SectionHeader>
      {controllers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 text-xs text-gray-500 dark:text-gray-400">
          No RAID controller detected (Dell OMSA / HPE ssacli / LSI storcli not installed on
          this host).
        </div>
      ) : (
        <div className="space-y-3">
          {controllers.map((c, i) => {
            const sev: Severity =
              (c.status || "").toLowerCase() === "ok"
                ? "ok"
                : (c.status || "").toLowerCase() === "failed"
                  ? "critical"
                  : (c.status || "").toLowerCase() === "degraded"
                    ? "warning"
                    : "na";
            return (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {c.vendor?.toUpperCase()} — {c.model || "(unknown)"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Controller id: {c.id || "?"}</div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${pillClass(sev)}`}
                  >
                    {(c.status || "unknown").toUpperCase()}
                  </span>
                </div>
                {(c.virtual_disks || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Virtual disks</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-700 dark:text-gray-300">
                      {c.virtual_disks!.map((vd, j) => (
                        <div key={j} className="border dark:border-gray-700 rounded px-2 py-1">
                          <span className="font-mono">{vd.id}</span> — {vd.raid_level || "?"} —{" "}
                          {vd.state || "?"}
                          {vd.size_gb ? ` — ${vd.size_gb.toFixed(0)} GB` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(c.physical_disks || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Physical disks</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-700 dark:text-gray-300">
                      {c.physical_disks!.map((pd, j) => (
                        <div key={j} className="border dark:border-gray-700 rounded px-2 py-1">
                          <span className="font-mono">{pd.slot}</span> — {pd.state || "?"}
                          {pd.model ? ` — ${pd.model}` : ""}
                          {pd.size_gb ? ` — ${pd.size_gb.toFixed(0)} GB` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── PSU ── */}
      <SectionHeader>Power Supplies</SectionHeader>
      {psus.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 text-xs text-gray-500 dark:text-gray-400">
          {psuEmptyMessage(device.last_psu_status?.reason)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {psus.map((p, i) => {
            const sev: Severity =
              (p.status || "").toLowerCase() === "ok"
                ? "ok"
                : (p.status || "").toLowerCase() === "failed"
                  ? "critical"
                  : (p.status || "").toLowerCase() === "degraded"
                    ? "warning"
                    : "na";
            return (
              <Tile
                key={i}
                title={p.id || `PSU ${i + 1}`}
                value={(p.status || "unknown").toUpperCase()}
                severity={sev}
                subtitle={p.watts != null ? `${p.watts}W` : null}
              />
            );
          })}
        </div>
      )}
      {psus.length > 0 && device.last_psu_status?.redundancy_ok != null && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Redundancy: {device.last_psu_status.redundancy_ok ? "OK" : "DEGRADED"}
        </div>
      )}
      {psus.length === 1 && device.last_psu_status?.redundancy_ok == null && (
        // Not a warning. One supply is the design of the chassis, not a
        // fault - saying "DEGRADED" here alarmed people about a healthy
        // server, which is how a monitoring tool loses their attention.
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Single supply — no redundancy on this chassis.
        </div>
      )}

      {procOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Running processes — {device.hostname}
                </h2>
                {!procLoading && !procError && (
                  <p className="text-xs text-gray-400">
                    {procRows.length} shown{procTotal > procRows.length ? ` of ${procTotal}` : ""} · sorted by{" "}
                    {procSort.sort.key === "cpu"
                      ? "CPU"
                      : procSort.sort.key === "disk_kbps"
                        ? "disk I/O"
                        : procSort.sort.key === "mem_mb"
                          ? "memory"
                          : procSort.sort.key}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadProcesses(procSort.sort.key as "cpu" | "mem_mb" | "disk_kbps")}
                  disabled={procLoading}
                  className="text-xs border border-gray-300 dark:border-gray-600 dark:text-gray-100 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                >
                  ↻ Refresh
                </button>
                <button
                  onClick={() => setProcOpen(false)}
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="px-5 py-2 border-b border-gray-100 dark:border-gray-700">
              <input
                type="text"
                value={procSearch}
                onChange={(e) => setProcSearch(e.target.value)}
                placeholder="Filter by name or PID…"
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {procLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-6 text-center">
                  Querying the device… (~a few seconds)
                </p>
              )}
              {procError && <p className="text-sm text-red-600 dark:text-red-400 p-6">{procError}</p>}
              {!procLoading && !procError && (
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                    <tr>
                      <SortableTh label="Process" sortKey="name" sort={procSort.sort} onSort={procSort.toggle} />
                      <SortableTh label="PID" sortKey="pid" sort={procSort.sort} onSort={procSort.toggle} />
                      <SortableTh label="User" sortKey="user" sort={procSort.sort} onSort={procSort.toggle} />
                      <SortableTh label="CPU %" sortKey="cpu" sort={procSort.sort} onSort={procSort.toggle} />
                      <SortableTh label="Memory" sortKey="mem_mb" sort={procSort.sort} onSort={procSort.toggle} />
                      <SortableTh label="Disk KB/s" sortKey="disk_kbps" sort={procSort.sort} onSort={procSort.toggle} />
                      {canManage && <th className="px-3 py-2"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
                    {sortRows(
                      procRows.filter((p) => {
                        const q = procSearch.trim().toLowerCase();
                        return (
                          !q ||
                          p.name.toLowerCase().includes(q) ||
                          String(p.pid).includes(q)
                        );
                      }),
                      procSort.sort,
                      (p, key) => {
                        switch (key) {
                          case "pid": return p.pid;
                          case "cpu": return p.cpu;
                          case "mem_mb": return p.mem_mb;
                          case "disk_kbps": return p.disk_kbps ?? 0;
                          case "user": return p.user || "";
                          default: return p.name.toLowerCase();
                        }
                      },
                    ).map((p) => (
                      <tr key={p.pid} className="group">
                        <td className="px-3 py-1.5 truncate max-w-[16rem]" title={p.name}>{p.name}</td>
                        <td className="px-3 py-1.5 font-mono">{p.pid}</td>
                        <td className="px-3 py-1.5 truncate max-w-[10rem]" title={p.user}>{p.user || "—"}</td>
                        <td className="px-3 py-1.5 font-mono text-right">{p.cpu?.toFixed(1)}%</td>
                        <td className="px-3 py-1.5 font-mono text-right">{p.mem_mb?.toFixed(0)} MB</td>
                        <td className="px-3 py-1.5 font-mono text-right">
                          {p.disk_kbps != null ? (p.disk_kbps >= 1024 ? `${(p.disk_kbps / 1024).toFixed(1)} MB/s` : `${p.disk_kbps.toFixed(0)} KB/s`) : "—"}
                        </td>
                        {canManage && (
                          <td className="px-3 py-1.5 text-right">
                            <button
                              onClick={() => killFromList(p.pid, p.name)}
                              disabled={killing === p.pid}
                              className="text-[11px] text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-40 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              {killing === p.pid ? "…" : "End task"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {killMsg && (
              <p className="px-5 py-2 text-[11px] text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700">
                {killMsg}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
